// -----------------------------------------------------------------------------
// filterIntent.ts
//
// The pure half of "describe what you want to see" on the job board: it turns the
// BFF's proposed filter (`POST api/assist/board-filter`) into the exact values the
// filter bar would hold if the operator had used the pickers themselves.
//
// It lives here, beside `jobFilters.ts`, for the reason that file gives: the boards
// and the bar are Syncfusion-heavy and awkward to exercise, so the logic goes in a
// tested helper and the components only apply the result. Nothing in here touches
// React, the store or the network.
//
// ⚠️ IT CHECKS THE MODEL'S VALUES AGAINST THE PICKERS A SECOND TIME, AND THAT IS
// DELIBERATE DUPLICATION. `AssistGuard` on the Function App matches every value
// against the option lists this client sent it and returns them in the list's own
// spelling. This does it again, against the lists the pickers are *currently*
// showing. Neither side may assume the other ran — the same rule the support
// assistant's chips follow — and here the two lists can genuinely differ: the
// warehouse list is fetched once per mount and the BP names are derived from the
// jobs currently on the board, so a reply can arrive describing a board that has
// since been refreshed.
//
// WHAT AN UNMATCHED VALUE COSTS, AND WHY IT IS DROPPED RATHER THAN APPLIED: the
// boards filter on an exact (case-insensitive) match against the job's own field,
// so a chip for a warehouse no job carries hides every card. To the operator that
// is an empty board, not a mistake — which is why an unmatched value is dropped and
// SAID OUT LOUD in `unresolved` instead.
// -----------------------------------------------------------------------------

import { addUnique, parseSearchValue } from "./jobFilters";
import { thisWeekRange, todayRange } from "./dateFilter";

/**
 * What the board's date filter should become.
 *
 * ⚠️ `KEEP` AND `NONE` ARE NOT THE SAME THING and collapsing them is the obvious
 * mistake. The bar applies **This Week** on first paint, so a request that said
 * nothing about dates ("show me warehouse 01") must leave that alone — reading
 * silence as "clear the dates" would quietly widen every filter to the whole board.
 */
export type DueIntent = "KEEP" | "NONE" | "TODAY" | "THIS_WEEK" | "RANGE";

/** The proposal, as it arrives from the BFF. Every field is optional but `duePreset`. */
export interface BoardFilterIntent {
  /** Empty every chip before applying the rest. */
  clear?: boolean;
  warehouses?: string[];
  resources?: string[];
  bpNames?: string[];
  duePreset?: DueIntent;
  /** Inclusive `YYYY-MM-DD`, for `RANGE` only. */
  dueFrom?: string | null;
  dueTo?: string | null;
  /** Text for the board's own search box. Usually absent. */
  text?: string | null;
  /** What the person named that matched nothing, in their own words. */
  unresolved?: string[];
}

/** What the pickers are offering right now. Resource filters are named, not objects. */
export interface BoardFilterOptions {
  warehouses: string[];
  resources: string[];
  bpNames: string[];
}

/** What the bar holds right now. */
export interface BoardFilterState {
  warehouses: string[];
  /** The selected resource filters, by name. */
  resources: string[];
  bpNames: string[];
  /** The pipe-delimited `appSettings.searchValue`, exactly as the store holds it. */
  searchValue?: string;
}

/** What to apply. Every list is complete — assign it, don't merge it. */
export interface BoardFilterPlan {
  warehouses: string[];
  resources: string[];
  bpNames: string[];
  /**
   * The composed `searchValue`, or `undefined` for "leave the store's alone".
   *
   * ⚠️ `undefined` IS A DISTINCT OUTCOME FROM `""` and the caller must not collapse
   * them: the empty string CLEARS the due-date range (and with it the Today/This
   * Week highlight, which `GlobalFilter` derives from the store), while undefined
   * is what `KEEP` means. Writing `""` for a request that never mentioned dates is
   * exactly how the whole board would open up underneath somebody.
   */
  searchValue?: string;
  /** One short line per thing that was applied, for the person to read. */
  applied: string[];
  /** Words that matched nothing — the model's list plus anything dropped here. */
  unresolved: string[];
}

/** Case/whitespace-insensitive comparison key. The same one `jobFilters` uses. */
const key = (value: string | null | undefined): string =>
  (value ?? "").trim().toLowerCase();

/**
 * Every proposed value that IS one of the options, in the OPTION'S spelling.
 *
 * Unmatched values are returned separately rather than thrown away, because the
 * only useful thing to do with one is tell the person it did not match.
 */
const matchOptions = (
  proposed: string[] | undefined,
  options: string[]
): { matched: string[]; missed: string[] } => {
  const matched: string[] = [];
  const missed: string[] = [];

  for (const value of proposed ?? []) {
    if (!value?.trim()) continue;

    const option = options.find((o) => key(o) === key(value));

    if (option) {
      if (!matched.some((m) => key(m) === key(option))) matched.push(option);
    } else if (!missed.some((m) => key(m) === key(value))) {
      missed.push(value.trim());
    }
  }

  return { matched, missed };
};

/**
 * Compose the store's pipe-delimited `searchValue` the way `GlobalFilter` does.
 *
 * ⚠️ THE FREE TEXT GOES LAST AND IS STRIPPED OF BARS. `parseSearchValue` splits on
 * `|` and treats any `__token__` as structured, taking the first remaining part as
 * the operator's text — so a bar inside the text would split it into a token of its
 * own and a `__` prefix would make it vanish. The Function App strips both as well;
 * this is the second of the two passes.
 *
 * It also carries the date AND the text together, which the bar has never done (its
 * own `composeSearchValue` writes only the date, and the search box overwrites the
 * whole value with bare text). Both boards already read the two separately out of
 * `parseSearchValue`, so the combination is what they were always able to handle —
 * it is the only way "find 4501234 due this week" can be one action.
 */
const compose = (
  preset: string | null,
  from: string | null,
  to: string | null,
  text: string | null
): string => {
  const parts: string[] = [];

  if (preset) parts.push(`__preset__:${preset}`);
  if (from && to) parts.push(`__date__:${from}:${to}`);

  const cleaned = (text ?? "").replace(/\|/g, " ").replace(/^(__)+/, "").trim();
  if (cleaned) parts.push(cleaned);

  return parts.join("|");
};

/**
 * Turn a proposal into the values to apply.
 *
 * `now` is a parameter so the two date presets are deterministic under test — the
 * same seam `buildQueryRequest` and `todayRange`/`thisWeekRange` take, and for the
 * same reason: these are LOCAL calendar days, and a helper that reads the clock
 * itself can only be asserted vacuously.
 */
export function planBoardFilter(
  intent: BoardFilterIntent | null | undefined,
  options: BoardFilterOptions,
  current: BoardFilterState,
  now: Date = new Date()
): BoardFilterPlan {
  const applied: string[] = [];

  // `clear` is the only thing that can REMOVE a chip the person set by hand, so it
  // is applied first and everything below builds on what is left.
  const base: BoardFilterState = intent?.clear
    ? { warehouses: [], resources: [], bpNames: [], searchValue: "" }
    : current;

  if (intent?.clear) applied.push("cleared the filters");

  const warehouses = matchOptions(intent?.warehouses, options.warehouses);
  const resources = matchOptions(intent?.resources, options.resources);
  const bpNames = matchOptions(intent?.bpNames, options.bpNames);

  const plan: BoardFilterPlan = {
    warehouses: warehouses.matched.reduce(addUnique, base.warehouses ?? []),
    resources: resources.matched.reduce(addUnique, base.resources ?? []),
    bpNames: bpNames.matched.reduce(addUnique, base.bpNames ?? []),
    applied,
    unresolved: [
      ...(intent?.unresolved ?? []),
      ...warehouses.missed,
      ...resources.missed,
      ...bpNames.missed,
    ],
  };

  if (warehouses.matched.length) {
    applied.push(`warehouse ${warehouses.matched.join(", ")}`);
  }
  if (resources.matched.length) {
    applied.push(`resource ${resources.matched.join(", ")}`);
  }
  if (bpNames.matched.length) {
    applied.push(`BP name ${bpNames.matched.join(", ")}`);
  }

  // ---- the due date, and the text that shares its field ----------------------
  const held = parseSearchValue(base.searchValue);
  const heldPreset = presetMarkerOf(base.searchValue);
  const text = intent?.text?.trim() ? intent.text.trim() : held.text;
  const due = intent?.duePreset ?? "KEEP";

  if (due === "KEEP") {
    // Nothing was said about dates. Recompose only when the text changed, so a
    // request that touched neither leaves the store's value untouched entirely —
    // see the note on `BoardFilterPlan.searchValue`.
    if (key(text) !== key(held.text) || intent?.clear) {
      plan.searchValue = compose(heldPreset, held.from, held.to, text);
    }
  } else if (due === "NONE") {
    plan.searchValue = compose(null, null, null, text);
    applied.push("any due date");
  } else if (due === "TODAY") {
    const [from, to] = todayRange(now);
    plan.searchValue = compose("TODAY", from, to, text);
    applied.push("due today");
  } else if (due === "THIS_WEEK") {
    const [from, to] = thisWeekRange(now);
    plan.searchValue = compose("THIS_WEEK", from, to, text);
    applied.push("due this week");
  } else if (due === "RANGE" && intent?.dueFrom && intent?.dueTo) {
    // No preset marker: a custom range is exactly what "not Today and not This
    // Week" means to the bar's highlight, which reads the marker back out of here.
    plan.searchValue = compose(null, intent.dueFrom, intent.dueTo, text);
    applied.push(`due ${intent.dueFrom} to ${intent.dueTo}`);
  } else if (due === "RANGE") {
    // A range missing an end is not a range. The server drops these too; this is
    // the second pass, and dropping it leaves the date filter as it was.
    if (key(text) !== key(held.text)) {
      plan.searchValue = compose(heldPreset, held.from, held.to, text);
    }
  }

  if (text && key(text) !== key(held.text)) {
    applied.push(`searching for ${text}`);
  }

  return plan;
}

/**
 * The `__preset__` marker already in a composed value, if any.
 *
 * `readDuePreset` in `jobFilters.ts` answers the same question but narrows to the
 * two the bar's buttons can light up; this keeps whatever is there so recomposing
 * a value cannot silently demote a preset to a custom range.
 */
const presetMarkerOf = (searchValue?: string | null): string | null => {
  for (const part of (searchValue ?? "").split("|")) {
    const token = part.trim();
    if (token.startsWith("__preset__")) {
      const value = token.split(":")[1];
      if (value) return value;
    }
  }

  return null;
};

/**
 * The one-line summary shown under the box: what was applied, and what was not.
 *
 * Both halves matter. A filter that quietly did four of the five things asked of it
 * looks exactly like one that did all five, and the person is looking at a board
 * they cannot check against a request they have already forgotten the wording of.
 */
export function describePlan(plan: BoardFilterPlan): string {
  const parts: string[] = [];

  if (plan.applied.length) parts.push(`Showing ${plan.applied.join(", ")}.`);

  if (plan.unresolved.length) {
    parts.push(`I couldn't match ${plan.unresolved.join(", ")}.`);
  }

  return parts.join(" ");
}
